import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { api, ApiError, ensureCsrf } from '@/api/client';
import { useToast } from '@/components/Toast';
import type { MessageResponse } from '@/api/types';

export function ResetPassword() {
  const [params] = useSearchParams();
  const tokenFromUrl = params.get('token');
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [password, setPassword] = useState('');
  const [done, setDone] = useState(false);

  const requestReset = useMutation({
    mutationFn: async () => {
      await ensureCsrf();
      return api<MessageResponse>('/auth/password/reset-request', {
        method: 'POST',
        body: { email },
      });
    },
    onSuccess: () => setSent(true),
  });

  const complete = useMutation({
    mutationFn: async () => {
      await ensureCsrf();
      return api<MessageResponse>('/auth/password/reset', {
        method: 'POST',
        body: { token: tokenFromUrl, new_password: password },
      });
    },
    onSuccess: () => setDone(true),
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        toast.push({ tone: 'danger', title: 'Reset failed', description: err.message });
      }
    },
  });

  if (tokenFromUrl) {
    return (
      <div className="auth-shell">
        <div className="auth-card">
          <div className="auth-card__brand">
            <span className="auth-card__brand-mark" aria-hidden />
            <span>Polymarket Surveillance</span>
            <div className="spacer" />
            <ThemeToggle />
          </div>
          {done ? (
            <>
              <h2 className="auth-card__title">Password updated</h2>
              <p className="auth-card__subtitle">All existing sessions have been signed out.</p>
              <Link to="/login">
                <Button variant="primary" block>
                  Sign in
                </Button>
              </Link>
            </>
          ) : (
            <>
              <h2 className="auth-card__title">Set a new password</h2>
              <form
                className="stack-5"
                onSubmit={(e) => {
                  e.preventDefault();
                  complete.mutate();
                }}
              >
                <Input
                  label="New password"
                  type="password"
                  autoComplete="new-password"
                  required
                  hint="At least 12 characters."
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <Button variant="primary" type="submit" block disabled={complete.isPending}>
                  {complete.isPending ? 'Saving…' : 'Update password'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="auth-card__brand">
          <span className="auth-card__brand-mark" aria-hidden />
          <span>Polymarket Surveillance</span>
          <div className="spacer" />
          <ThemeToggle />
        </div>
        {!sent ? (
          <>
            <h2 className="auth-card__title">Reset password</h2>
            <p className="auth-card__subtitle">
              Enter your account email. If it's registered, we'll send a single-use reset link.
            </p>
            <form
              className="stack-5"
              onSubmit={(e) => {
                e.preventDefault();
                requestReset.mutate();
              }}
            >
              <Input
                label="Email"
                type="email"
                name="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Button variant="primary" type="submit" block disabled={requestReset.isPending}>
                {requestReset.isPending ? 'Sending…' : 'Send link'}
              </Button>
            </form>
          </>
        ) : (
          <>
            <h2 className="auth-card__title">Check your email</h2>
            <p className="auth-card__subtitle">
              If that account exists, a reset link is on its way. The link expires in 30 minutes.
            </p>
            <Link to="/login">
              <Button variant="ghost" block>
                Back to sign in
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
